import React, { FC, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Switch, useHistory } from 'react-router-dom';
import { SupportAgentBanner, UserRoute } from '../../components';
import { useTranslation } from '../../translations';
import { checkPermission, useTypeSequence, useUserSafe } from '../../utils';
import {
  CashierContext,
  CashierContextProps,
  CashierSettingsModel,
  defaultContext,
  getReceiptPrinterManagers,
  loadCashierSettings,
  saveCashierSettings,
} from './cashiercontext';
import { Header } from './header';
import { NFCController } from './nfc/NFC';
import { NFCSaveStatus } from './nfc/reducer';
import { PrinterManagerStatus } from './printers/PrinterBase';
import { cashierReducer } from './reducer/reducer';
import { ScannerContext, useCreateScannerContext } from './scanner';
import { ZebraPrinter2 } from './zebra/ZebraPrinter';
import { ZebraSaveStatus } from './zebra/reducer';
import { CashierHome, CashierProfile } from '.';

export const CashierComponent: FC = () => {
  const history = useHistory();
  const user = useUserSafe();
  const { ts } = useTranslation();
  const [state, dispatch] = useReducer(cashierReducer, {});

  const receiptPrinterManagers = useMemo(() => getReceiptPrinterManagers(), []);

  const [cashierSettings, setCashierSettings] = useState<CashierSettingsModel>(() => {
    return loadCashierSettings(defaultContext);
  });

  useEffect(() => {
    const decodedJSON = JSON.parse(localStorage.getItem('chasier') || '{}') as {
      nfc?: NFCSaveStatus;
      zebra?: ZebraSaveStatus;
    };

    dispatch({
      type: 'INIT_ZEBRA_PRINTER',
      payload: {
        manager: new ZebraPrinter2(dispatch, undefined),
        printers: [],
        // TODO: Need pending state?
        status: PrinterManagerStatus.Error,
        printQueue: [],
        rendering: false,
        saveState: decodedJSON.zebra || {},
        primaryPrinter: decodedJSON.zebra?.primaryPrinter,
        secondaryPrinter: decodedJSON.zebra?.secondaryPrinter,
      },
      source: 'zebra',
    });

    const nfcState = decodedJSON.nfc;
    const manager = new NFCController(dispatch, ts, nfcState);

    dispatch({
      type: 'INIT_NFC',
      payload: {
        manager,
        showNfcWarning: false,
        status: 'unknown',
        badgeForWarning: undefined,
        saveState: decodedJSON.nfc || { interfaces: [] },
        currentNfcTagInterface: manager.NFCTagInterfaces.find(
          (t) => t.originalDeviceUID === nfcState?.current,
        ),
      },
      source: 'nfc',
    });

    return () => {
      manager.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.nfcScanner?.saveState) {
      return;
    }

    localStorage.setItem(
      'chasier',
      JSON.stringify({
        nfc: state.nfcScanner.saveState,
        zebra: state.zebraPrinter?.saveState,
      }),
    );
  }, [state.nfcScanner?.saveState, state.zebraPrinter?.saveState]);

  useEffect(() => {
    saveCashierSettings(cashierSettings);
  }, [cashierSettings]);

  useTypeSequence(
    '@ANSI',
    'Enter',
    (value) => {
      if (!checkPermission(user, 'registration:cashier')) {
        return;
      }

      history.push(`/housekeeping/cashier?term=${encodeURIComponent(value)}`);
    },
    [user, history],
  );

  useTypeSequence(
    '@CONCAT',
    'Enter',
    (value) => {
      if (!checkPermission(user, 'registration:cashier')) {
        return;
      }

      history.push(`/housekeeping/cashier?term=${encodeURIComponent(value)}`);
    },
    [user, history],
  );

  const updateCashierSettings = useCallback(
    (settings: Partial<CashierSettingsModel>) => {
      const newSettings = {
        ...cashierSettings,
        ...settings,
      };

      setCashierSettings(newSettings);
      saveCashierSettings(newSettings);
    },
    [setCashierSettings, cashierSettings],
  );

  const scannerContext = useCreateScannerContext();

  const settings = useMemo(
    (): CashierContextProps => ({
      receiptPrinterManagers,
      config: cashierSettings,
      updateCashierSettings,
      state,
      dispatch,
    }),
    [cashierSettings, receiptPrinterManagers, updateCashierSettings, state, dispatch],
  );

  useEffect(() => {
    dispatch({
      type: 'NFC_SET_ACTIVE_INTERFACE',
      source: 'nfc',
      deviceUID: settings.config.NFCTagInterface,
    });
  }, [settings.config.NFCTagInterface]);

  return (
    <CashierContext.Provider value={settings}>
      <ScannerContext.Provider value={scannerContext}>
        <div className="w-100">
          <Header />
          <SupportAgentBanner />
          <Switch>
            <UserRoute
              component={CashierHome}
              exact
              path="/housekeeping/cashier"
              requirePermissions="registration:cashier"
            />
            <UserRoute
              component={CashierProfile}
              exact
              path="/housekeeping/cashier/:id"
              requirePermissions="registration:cashier"
            />
          </Switch>
        </div>
      </ScannerContext.Provider>
    </CashierContext.Provider>
  );
};
